import firebase from 'firebase/app';
import 'firebase/firestore';
import API from '.';

// Live listener class
import Live from './_live';

class groupSessions extends Live {
  api: API;
  constructor(api: API) {
    super();

    // Setup a reference to the API
    this.api = api;
  }

  mount(): void {
    // Setup listeners
    const role = this.api.credentials?.role;
    const currentUserId = firebase.auth().currentUser?.uid;

    if (role && ['student', 'tutor'].includes(role) && currentUserId) {
      this.listener(
        'groupsessionsMyLiveBookings',
        firebase.firestore().collection('groupsessions')
      );
    }
  }

  async searchGroupSessions(query: any): Promise<any> {
    // Determine the user's local timezone dynamically
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Include the userTimezone in the query payload
    const payload = {
      data: query,
      timezone: userTimezone
    };

    return this.api._http(
      'groupsessions/search',
      'POST',
      { data: payload },
      false
    );
  }

  /*
    API endpoint for making a booking
  */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async bookGroupSessionBooking(
    booking: any,
    selectedLessons: string[]
  ): Promise<void> {
    // Check whether we're the right role before making the request
    this.api.enforceRole('student');

    console.log('booking', booking, selectedLessons);

    // Make the request and get the response
    return await this.api._http(
      `groupsessions/session/book`,
      'PUT',
      { data: { booking, selectedLessons } },
      false
    );
  }

  async getGroupSessionsHistory(
    startDate: number,
    endDate: number
  ): Promise<any[]> {
    const role = this.api.credentials?.role;
    if (!role || !['student', 'tutor'].includes(role)) {
      throw new Error('Unauthorized access');
    }

    const currentUserId = firebase.auth().currentUser?.uid;
    if (!currentUserId) {
      throw new Error('User not authenticated');
    }

    const groupSessionsSnapshot = await firebase
      .firestore()
      .collection('groupsessions')
      .get();

    const groupSessionsHistory: any[] = [];

    groupSessionsSnapshot.forEach((doc) => {
      const groupSession = doc.data();
      const groupSessionId = doc.id;
      const sessionBookings = groupSession.bookings || [];

      const filteredBookings = sessionBookings.filter((booking: any) => {
        const bookingWhen = (() => {
          if (!booking.when) return 0;

          if (typeof booking.when?.toMillis === 'function') {
            return booking.when.toMillis();
          }

          if (typeof booking.when === 'number' && booking.when < 1e12) {
            return booking.when * 1000;
          }

          if (typeof booking.when === 'string') {
            return new Date(booking.when).getTime();
          }

          return booking.when as number;
        })();

        const isValidStatus = [
          'tutor-absent',
          'student-absent',
          'pending-feedback',
          'completed',
          'cancelled'
        ].includes(booking.status);

        const isWithinRange =
          bookingWhen >= startDate && bookingWhen <= endDate;

        if (role === 'tutor') {
          return (
            isValidStatus &&
            isWithinRange &&
            booking.tutor?.uid === currentUserId
          );
        }

        if (role === 'student') {
          return (
            isValidStatus &&
            isWithinRange &&
            booking?.studentIds?.includes(currentUserId)
          );
        }

        return false;
      });

      if (filteredBookings.length > 0) {
        groupSessionsHistory.push({
          id: groupSessionId,
          ...groupSession,
          bookings: filteredBookings
        });
      }
    });

    const sorted = groupSessionsHistory.map((session) => ({
      ...session,
      bookings: session.bookings.sort((a: any, b: any) => {
        const aWhen =
          typeof a.when === 'string'
            ? new Date(a.when).getTime()
            : typeof a.when?.toMillis === 'function'
            ? a.when.toMillis()
            : a.when;
        const bWhen =
          typeof b.when === 'string'
            ? new Date(b.when).getTime()
            : typeof b.when?.toMillis === 'function'
            ? b.when.toMillis()
            : b.when;
        return bWhen - aWhen; // Changed to descending order (latest first)
      })
    }));

    return sorted.sort((a, b) => {
      const aLatest = Math.max(
        // Changed from Math.min to Math.max for latest
        ...a.bookings.map((bk: any) =>
          typeof bk.when === 'string'
            ? new Date(bk.when).getTime()
            : typeof bk.when?.toMillis === 'function'
            ? bk.when.toMillis()
            : bk.when
        )
      );
      const bLatest = Math.max(
        // Changed from Math.min to Math.max for latest
        ...b.bookings.map((bk: any) =>
          typeof bk.when === 'string'
            ? new Date(bk.when).getTime()
            : typeof bk.when?.toMillis === 'function'
            ? bk.when.toMillis()
            : bk.when
        )
      );
      return bLatest - aLatest; // Changed to descending order (latest first)
    });
  }

  async joinGroupSession(id: string, bookingId: string) {
    // Run the HTTP function
    return this.api._http(
      `groupsessions/groupsession/${id}/${bookingId}/join`,
      'PUT',
      { data: {} },
      false
    );
  }
  async submitFeedback(
    groupid: string,
    bookingId: string,
    feedback: any
  ): Promise<void> {
    // Check whether we're the right role before making the request
    this.api.enforceRole('tutor');

    //get group session by id
    const groupSession = await firebase
      .firestore()
      .collection('groupsessions')
      .doc(groupid)
      .get();

    const updatedBookings = groupSession
      .data()
      ?.bookings.map((booking: any) => {
        if (booking.bookingId === bookingId) {
          const presentStudents = booking.students.filter((studnt: any) => {
            return studnt.status !== 'student-absent';
          });
          const presentstudentIds = presentStudents.map(
            (studnt: any) => studnt.id
          );
          const updatedStudents = booking.students.map((student: any) => {
            if (presentstudentIds.includes(student.id)) {
              return {
                ...student,
                feedback,
                status: 'completed'
              };
            }
            return student;
          });

          const allStudentsHaveFeedback = updatedStudents.some(
            (stu: any) => stu.feedback
          );
          console.log({
            ...booking,
            students: updatedStudents,
            status: allStudentsHaveFeedback ? 'completed' : 'pending-feedback'
          });
          return {
            ...booking,
            students: updatedStudents,
            status: allStudentsHaveFeedback ? 'completed' : 'pending-feedback'
          };
        }
        return booking;
      });

    // update group session with updated bookings
    await firebase
      .firestore()
      .collection('groupsessions')
      .doc(groupid)
      .update({ bookings: updatedBookings });
  }
  async viewFeedback(id: string, bookingId: string): Promise<any> {
    try {
      // Check whether we're the right role before making the request
      this.api.enforceRole('student');

      const currentUserId = firebase.auth().currentUser?.uid;
      const groupSession = await firebase
        .firestore()
        .collection('groupsessions')
        .doc(id)
        .get();

      const updatedBookings = groupSession
        .data()
        ?.bookings.map((booking: any) => {
          if (booking.bookingId === bookingId) {
            const updatedStudents = booking.students.map((student: any) => {
              if (student.id === currentUserId) {
                return {
                  ...student,
                  viewFeedback: true,
                  status: 'completed'
                };
              }
              return student;
            });

            return {
              ...booking,
              students: updatedStudents
            };
          }
          return booking;
        });

      // update group session with updated bookings
      await firebase
        .firestore()
        .collection('groupsessions')
        .doc(id)
        .update({ bookings: updatedBookings });
    } catch (error) {
      // Handle errors, log them, or throw a specific error based on your needs
      console.error('Error retrieving or updating feedback:', error);
      throw error;
    }
  }

  async cancelGroupSessionBooking(
    groupSessionId: string,
    bookingId: string,
    reason: string
  ): Promise<void> {
    // Check whether we're the right role before making the request
    this.api.enforceRole('student');
    // Make the request and get the response
    return await this.api._http(
      `groupsessions/cancel/${groupSessionId}/${bookingId}`,
      'PUT',
      { data: { reason } },
      false
    );
  }
}

export default groupSessions;
